import { create } from "zustand";
import { emissionProfilesApi } from "api/endpoints";

/**
 * Ambiente SII (certificación / producción) derivado de perfiles de emisión DTE.
 * Un solo fetch compartido: navbar, sidebar y futuros consumidores leen aquí (sin Redux).
 */
const PROFILE_PICK_STORAGE_KEY = "fe-selected-emission-profile-uuid";

function readStoredProfileUuid() {
  if (typeof window === "undefined") return "";
  try {
    return String(window.localStorage.getItem(PROFILE_PICK_STORAGE_KEY) || "").trim();
  } catch {
    return "";
  }
}

function writeStoredProfileUuid(profileUuid) {
  if (typeof window === "undefined") return;
  try {
    const v = String(profileUuid || "").trim();
    if (!v) {
      window.localStorage.removeItem(PROFILE_PICK_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(PROFILE_PICK_STORAGE_KEY, v);
  } catch {
    // Ignorar errores de storage (modo privado, etc.).
  }
}

function normalizeProfile(item = {}) {
  return {
    id: item.id ?? null,
    profileUuid: item.profile_uuid ? String(item.profile_uuid).trim() : "",
    internalId: item.internal_id ? String(item.internal_id).trim() : "",
    rutEmisor: item.rut_emisor ? String(item.rut_emisor).trim() : "",
    ambiente: item.ambiente ? String(item.ambiente).trim().toLowerCase() : "",
    profileName:
      item.profile_name && String(item.profile_name).trim()
        ? String(item.profile_name).trim()
        : "",
    status: item.estado ? String(item.estado).trim().toLowerCase() : "",
    isDefault: Boolean(item.is_default),
  };
}

function sortProfiles(items = []) {
  return [...items].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    const byName = String(a.profileName || a.internalId || "")
      .localeCompare(String(b.profileName || b.internalId || ""), "es", { sensitivity: "base" });
    if (byName !== 0) return byName;
    return Number(a.id || 0) - Number(b.id || 0);
  });
}

function pickSelectedProfile(profiles = [], preferredProfileUuid = "") {
  if (!profiles.length) return null;
  const preferred = String(preferredProfileUuid || "").trim();
  if (preferred) {
    const exact = profiles.find((p) => p.profileUuid === preferred);
    if (exact) return exact;
  }
  const stored = readStoredProfileUuid();
  if (stored) {
    const exact = profiles.find((p) => p.profileUuid === stored);
    if (exact) return exact;
  }
  return profiles[0];
}

export const useEmissionAmbienteStore = create((set) => ({
  kind: "idle",
  mixed: undefined,
  ambiente: undefined,
  profileUuid: undefined,
  profileInternalId: undefined,
  rutEmisor: undefined,
  profiles: [],

  reset: () =>
    set({
      kind: "idle",
      mixed: undefined,
      ambiente: undefined,
      profileUuid: undefined,
      profileInternalId: undefined,
      rutEmisor: undefined,
      profiles: [],
    }),

  setSelectedProfileUuid: (nextProfileUuid) =>
    set((state) => {
      if (state.kind !== "ready") return state;
      const selected = pickSelectedProfile(state.profiles, nextProfileUuid);
      if (!selected) return state;
      writeStoredProfileUuid(selected.profileUuid);
      return {
        ...state,
        profileUuid: selected.profileUuid || null,
        profileInternalId: selected.internalId || null,
        rutEmisor: selected.rutEmisor || null,
        ambiente: selected.ambiente || null,
      };
    }),

  fetchForUser: async ({ organizationId, signal, preferredProfileUuid, forceAmbiente } = {}) => {
    set({
      kind: "loading",
      mixed: undefined,
      ambiente: undefined,
      profileUuid: undefined,
      profileInternalId: undefined,
      rutEmisor: undefined,
      profiles: [],
    });
    try {
      if (!organizationId) {
        set({
          kind: "empty",
          mixed: undefined,
          ambiente: undefined,
          profileUuid: undefined,
          profileInternalId: undefined,
          rutEmisor: undefined,
          profiles: [],
        });
        return;
      }
      const { res, data } = await emissionProfilesApi.list({
        estado: "active",
        organization_id: organizationId,
      });
      if (signal?.aborted) return;
      const items = Array.isArray(data?.items) ? data.items : [];
      if (signal?.aborted) return;
      if (!res.ok) {
        set({
          kind: "error",
          mixed: undefined,
          ambiente: undefined,
          profileUuid: undefined,
          profileInternalId: undefined,
          rutEmisor: undefined,
          profiles: [],
        });
        return;
      }
      if (items.length === 0) {
        set({
          kind: "empty",
          mixed: undefined,
          ambiente: undefined,
          profileUuid: undefined,
          profileInternalId: undefined,
          rutEmisor: undefined,
          profiles: [],
        });
        return;
      }
      let normalizedProfiles = sortProfiles(items.map(normalizeProfile));
      // Restricción por rol (p. ej. contador): solo perfiles del ambiente forzado
      // (producción). Así el switch nunca ofrece certificación -> no puede cambiar de modo.
      const forced = String(forceAmbiente || "").trim().toLowerCase();
      if (forced) {
        normalizedProfiles = normalizedProfiles.filter((p) => p.ambiente === forced);
      }
      if (normalizedProfiles.length === 0) {
        set({
          kind: "empty",
          mixed: undefined,
          ambiente: forced || undefined,
          profileUuid: undefined,
          profileInternalId: undefined,
          rutEmisor: undefined,
          profiles: [],
        });
        return;
      }
      const ambientes = [
        ...new Set(normalizedProfiles.map((i) => i.ambiente).filter(Boolean)),
      ];
      const selectedProfile = pickSelectedProfile(normalizedProfiles, preferredProfileUuid);
      writeStoredProfileUuid(selectedProfile?.profileUuid || "");
      set({
        kind: "ready",
        mixed: ambientes.length > 1,
        ambiente: selectedProfile?.ambiente || null,
        profileUuid: selectedProfile?.profileUuid || null,
        profileInternalId: selectedProfile?.internalId || null,
        rutEmisor: selectedProfile?.rutEmisor || null,
        profiles: normalizedProfiles,
      });
    } catch {
      if (!signal?.aborted) {
        set({
          kind: "error",
          mixed: undefined,
          ambiente: undefined,
          profileUuid: undefined,
          profileInternalId: undefined,
          rutEmisor: undefined,
          profiles: [],
        });
      }
    }
  },
}));
